<template>
  <div class="container mx-auto">
    <!-- Barra de búsqueda y filtros -->
    <div class="flex justify-between mb-4" v-if="mode === 'cart'">
      <text-input 
        placeholder="Buscar producto por nombre o código..." 
        id="search" 
        class="p-2 border border-gray-300 rounded w-2/3"
        v-model="form.search.terms"
        autofocus
      />
      <select 
        v-model="form.search.category" 
        class="p-2 border border-gray-300 rounded w-1/3 ml-2"
      >
        <option value="">Todas las categorías</option>
        <option 
          v-for="category in product_categories" 
          :key="category.id" 
          :value="category.id"
        >
          {{ category.name }}
        </option>
      </select>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 gap-4">
      <!-- Lista de productos -->
      <div class="col-span-2">
        <div v-if="mode === 'cart'" class="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 max-h-[600px] overflow-y-auto">
          <product-card 
            v-for="product in products.data" 
            :key="product.id" 
            :product="product" 
            @addCart="addCart" 
            :change="change" 
            :cop_change="cop_change"
          />
        </div>
      </div>

      <!-- Carrito de compras -->
      <div v-if="mode === 'cart'" class="bg-white rounded-lg shadow-lg p-6">
        <h2 class="text-xl font-bold mb-6">Carrito de Compras</h2>

        <div class="space-y-4 max-h-[400px] overflow-y-auto">
          <div 
            v-for="(product, key) in cart" 
            :key="product.id" 
            class="flex flex-col p-4 border rounded-lg bg-white shadow-sm hover:shadow transition-shadow"
          >
            <!-- Header: Name, Stock, Delete -->
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1 pr-2">
                <h3 class="font-bold text-gray-800 leading-tight">{{ product?.name }}</h3>
                <p class="text-xs text-gray-500 mt-1">Stock: {{ product?.stock }} unidades</p>
              </div>
              <button 
                @click="removeProduct(product)" 
                class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                title="Eliminar producto"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>

            <!-- Controls: Quantity & Price Type -->
            <div class="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label class="block text-xs text-gray-500 mb-1">Cantidad</label>
                <text-input-rounded 
                  type="number" 
                  :max="product?.stock"
                  min="1"
                  v-model="cart[key]['quantity']" 
                  class="w-full text-center" 
                />
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1">Tipo de Precio</label>
                <select 
                  v-model="cart[key]['priceType']"
                  @change="onPriceTypeChange(key)"
                  class="w-full rounded-md border-gray-300 text-sm py-1.5 px-2"
                >
                  <option 
                    v-for="group in groups" 
                    :key="group.id" 
                    :value="'group-'+group.id"
                  >
                    {{ group.name }} - ${{ getGroupPrice(product.prices, group.id) }}
                  </option>
                  <option v-if="$page.props.auth.user.role !== 'sales'" value="custom">Personalizado</option>
                </select>
              </div>
            </div>

            <!-- Price Input and Total -->
            <div class="flex items-center justify-between border-t pt-3 mt-1">
              <div class="flex items-center gap-1.5 w-1/2">
                <span class="text-gray-500 font-medium">$</span>
                <input
                  v-model="cart[key]['price']"
                  type="number"
                  :disabled="cart[key]['priceType'] !== 'custom' || (!product.quick && $page.props.auth.user.role === 'sales')"
                  class="w-full text-right rounded-md border-gray-300 py-1.5 px-2 text-sm font-medium focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                />
              </div>
              <div class="text-right w-1/2 pl-2">
                <div class="text-xs text-gray-500">Total</div>
                <div class="text-base font-bold text-gray-900">
                  ${{ (cart[key]['price'] * product.quantity).toFixed(2) }}
                </div>
              </div>
            </div>

            <div v-if="product.require_note" class="mt-3">
              <input 
                type="text" 
                v-model="cart[key]['note']" 
                class="w-full p-2 border border-gray-300 rounded-md text-sm bg-gray-50 focus:bg-white" 
                placeholder="Nota requerida *"
                required
              />
            </div>
          </div>

          <div v-if="!cart.length" class="text-center text-gray-500 py-8">
            El carrito está vacío
          </div>
        </div>

        <!-- Totales -->
        <div class="mt-6 pt-4 border-t space-y-2">
          <div class="flex justify-between font-medium">
            <span>Total USD:</span>
            <span>${{ getTotal() }}</span>
          </div>
          <div class="flex justify-between text-gray-600">
            <span>Total Bs:</span>
            <span>{{ (getTotal() * change).toFixed(2) }}</span>
          </div>
          <div class="flex justify-between text-gray-600">
            <span>Total COP:</span>
            <span>{{ ((getTotal() * change) * cop_change).toFixed(1) }}</span>
          </div>
        </div>

        <div class="mt-4 flex flex-col gap-4">
          <div class="flex items-center">
            <input 
              type="checkbox" 
              v-model="withoutClient" 
              id="withoutClient"
              class="rounded border-gray-300"
            >
            <label for="withoutClient" class="ml-2 text-gray-700">
              Venta sin cliente registrado
            </label>
          </div>

          <div>
            <label class="block text-gray-700 mb-1 text-sm font-medium">Observación (Opcional)</label>
            <textarea 
              v-model="form.comentary" 
              class="w-full p-2 border border-gray-300 rounded-md text-sm" 
              rows="2" 
              placeholder="Ingrese observaciones importantes..."
            ></textarea>
          </div>
        </div>
      </div>

      <!-- Formulario de cliente -->
      <div v-if="mode === 'client'" class="col-span-3 bg-white rounded-lg shadow-lg p-6">
        <div class="flex items-center justify-between mb-4 border-b pb-4">
          <h2 class="text-xl font-bold text-gray-800">Datos del Cliente</h2>
          <button 
            type="button"
            @click="clearAllClientFields"
            class="px-4 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg shadow-sm flex items-center gap-2 transition-colors cursor-pointer"
            title="Limpiar todas las celdas del formulario"
          >
            <i class="fa fa-trash-alt text-gray-500"></i>
            <span>Limpiar todas las celdas</span>
          </button>
        </div>

        <!-- Aviso si el cliente tiene saldo pendiente / deuda registrada -->
        <div v-if="form.client.balance && parseFloat(form.client.balance) !== 0" class="mb-4 p-3 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg flex items-center justify-between shadow-sm">
          <div class="flex items-center gap-2 text-amber-900 text-sm">
            <i class="fa fa-exclamation-triangle text-amber-600 text-base"></i>
            <span>
              Saldo registrado del cliente: 
              <strong class="font-bold">
                {{ parseFloat(form.client.balance) > 0 ? `Deuda pendiente de $${parseFloat(form.client.balance).toFixed(2)}` : `Saldo a favor de $${Math.abs(parseFloat(form.client.balance)).toFixed(2)}` }}
              </strong>
            </span>
          </div>
          <button 
            type="button" 
            @click="resetBalance" 
            class="text-amber-800 hover:text-amber-950 text-xs font-semibold px-2.5 py-1 rounded bg-amber-200 hover:bg-amber-300 transition-colors flex items-center gap-1 cursor-pointer"
            title="Descartar aviso de saldo"
          >
            <i class="fa fa-times"></i>
            <span>Descartar</span>
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
          <!-- Identificación o Alias -->
          <div class="flex items-end gap-2">
            <text-input 
              v-model="form.client.identification" 
              label="Identificación o Alias"
              class="flex-1"
            />
            <button 
              class="btn-blue h-10 px-4 flex items-center justify-center gap-1.5"
              @click="findClient" 
              type="button"
              title="Buscar cliente"
            >
              <i class="fa fa-search" />
            </button>
            <button 
              type="button"
              @click="form.client.identification = ''"
              class="h-10 px-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:text-red-600 rounded flex items-center justify-center transition-colors cursor-pointer"
              title="Limpiar Identificación"
            >
              <i class="fa fa-trash-alt text-sm" />
            </button>
          </div>

          <!-- Nombre Completo -->
          <div class="flex items-end gap-2">
            <text-input 
              v-model="form.client.name" 
              label="Nombre Completo" 
              class="flex-1"
            />
            <button 
              type="button"
              @click="form.client.name = ''"
              class="h-10 px-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:text-red-600 rounded flex items-center justify-center transition-colors cursor-pointer"
              title="Limpiar Nombre Completo"
            >
              <i class="fa fa-trash-alt text-sm" />
            </button>
          </div>

          <!-- Teléfono -->
          <div class="flex items-end gap-2">
            <text-input 
              v-model="form.client.phone" 
              label="Teléfono" 
              class="flex-1"
            />
            <button 
              type="button"
              @click="form.client.phone = ''"
              class="h-10 px-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:text-red-600 rounded flex items-center justify-center transition-colors cursor-pointer"
              title="Limpiar Teléfono"
            >
              <i class="fa fa-trash-alt text-sm" />
            </button>
          </div>

          <!-- Dirección -->
          <div class="flex items-end gap-2">
            <text-input 
              v-model="form.client.address" 
              label="Dirección" 
              class="flex-1"
            />
            <button 
              type="button"
              @click="form.client.address = ''"
              class="h-10 px-3 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 hover:text-red-600 rounded flex items-center justify-center transition-colors cursor-pointer"
              title="Limpiar Dirección"
            >
              <i class="fa fa-trash-alt text-sm" />
            </button>
          </div>
        </div>
      </div>

      <!-- Datos de pago -->
      <div v-if="mode === 'pay'" class="bg-white rounded-lg shadow-lg p-6">
        <h2 class="text-xl font-bold mb-6">Método de Pago</h2>

        <div class="space-y-4">
          <div>
            <label class="block text-gray-700 mb-2">Seleccione método de pago</label>
            <select 
              v-model="form.method"
              class="form-select w-full rounded-lg border-gray-300"
            >
              <option value="">Seleccionar método</option>
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia Bs</option>
              <option value="transferencia-usd">Transferencia USD</option>
              <option value="biopago">Zelle</option>
              <option value="credito">Crédito</option>
            </select>
          </div>

          <text-input 
            v-if="form.method && form.method !== 'efectivo'"
            v-model="form.referencePay" 
            label="Número de Referencia"
          />

          <text-input 
            v-if="form.method === 'efectivo'"
            v-model="cambio"
            type="number"
            label="Monto recibido" 
          />
        </div>
      </div>

      <!-- Comprobantes -->
      <div v-if="mode === 'print'" class="bg-white rounded-lg shadow-lg p-6">
        <h2 class="text-xl font-bold mb-6">Comprobantes de Pago</h2>
        
        <div class="flex gap-4">
          <button class="btn-blue" type="button">
            <i class="fa fa-print mr-2"></i> Nota de entrega
          </button>
          <button class="btn-blue" type="button">
            <i class="fa fa-print mr-2"></i> Factura
          </button>
          <button class="btn-blue" type="button">
            <i class="fa fa-print mr-2"></i> Ticket
          </button>
        </div>
      </div>
    </div>

    <!-- Barra inferior fija -->
    <div class="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-4 flex items-center justify-between">
      <div class="space-x-4">
        <span v-if="cambio" class="font-medium">
          Cambio: ${{ (cambio - getTotal()).toFixed(2) }}
        </span>
        <span class="font-medium">
          Total: ${{ getTotal() }} | Bs.{{ (getTotal() * change).toFixed(2) }} | 
          COP {{ ((getTotal() * change) * cop_change).toFixed(1) }}
        </span>
      </div>

      <div class="space-x-2">
        <button 
          v-if="mode !== 'cart'"
          @click="back"
          class="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg"
        >
          Atrás
        </button>
        <button 
          @click="next"
          class="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
        >
          Siguiente
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LayoutFull from '../../Shared/LayoutFull.vue'
import ProductCard from '../../Shared/ProductCard.vue'
import TextInput from '../../Shared/TextInput.vue'
import TextInputRounded from '../../Shared/TextInputRounded.vue'
import pickBy from 'lodash/pickBy'
import SelectInput from '../../Shared/SelectInput.vue'

export default {
  components: { 
    ProductCard, 
    TextInput, 
    TextInputRounded, 
    SelectInput 
  },

  layout: LayoutFull,

  props: {
    products: Object,
    change: Number,
    cop_change: Number,
    client: Object,
    product_categories: Object,
    groups: Array,
    default_generic_client: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      cambio: 0,
      showClientCreate: false,
      mode: 'cart',
      form: this.$inertia.form({
        search: {
          terms: '',
          category: ''
        },
        client: {
          identification: '',
          name: '',
          email: '',
          phone: '',
          address: '',
          balance: 0,
          typeClient: 'a'
        },
        products: null,
        isCredit: false,
        comentary: '',
        referencePay: '',
        quick: true
      }),
      cart: [],
      withoutClient: this.default_generic_client
    }
  },

  watch: {
    'form.search.terms': function() {
      this.onSearchProductsChange()
    },

    'form.search.category': function() {
      this.onSearchProductsChange()
    },

    'products': function(val) {
      if(this.products.data.length === 1) {
        const product = this.products.data[0]
        
        if(product.code === this.form.search.terms) {
          if (product.quantity <= 0) {
            this.form.search.terms = ''
            return alert('No hay stock disponible de este producto')
          }

          const existingProduct = this.cart.find(p => p.id === product.id)
          
          const defaultPrice = product?.prices?.length ? product.prices[0].price : 0
          const defaultGroupId = product?.prices?.length ? product.prices[0].group_id : null

          if (existingProduct) {
            existingProduct.quantity++
          } else {
            this.cart.push({
              ...product,
              stock: product.quantity,
              quantity: 1,
              price: defaultPrice,
              priceType: defaultGroupId ? `group-${defaultGroupId}` : 'custom',
              quick: false
            })
          }

          this.form.search.terms = ''
        }
      }
    }
  },

  methods: {
    getGroupPrice(prices, groupId) {
      const match = prices?.find(p => p.group_id === groupId)
      return match ? match.price : '-'
    },
    
    onPriceTypeChange(index) {
      const product = this.cart[index]
      if (!product) return

      if (product.priceType === 'custom') {
        if (this.$page.props.auth.user.role === 'sales') {
          const fallbackGroupId = product?.prices?.length ? product.prices[0].group_id : null
          product.priceType = fallbackGroupId ? `group-${fallbackGroupId}` : null
        }
        return
      }

      if (product.priceType && product.priceType.startsWith('group-')) {
        const groupId = parseInt(product.priceType.replace('group-', ''))
        const price = this.getGroupPrice(product.prices, groupId)
        if (price !== '-') {
          this.cart[index].price = price
        }
      }
    },
    getTotal() {
      return this.cart.reduce((total, product) => {
        return total + (product.price * product.quantity)
      }, 0).toFixed(2)
    },

    back() {
      if(this.mode === 'client') {
        this.mode = 'cart'
      }
    },

    next() {
      if(this.mode === 'cart') {
        if(this.cart.length === 0) {
          return alert("Debe agregar productos al carrito para continuar")
        }

        const missingNote = this.cart.find(p => p.require_note && (!p.note || p.note.trim() === ''));
        if (missingNote) {
          return alert(`El producto ${missingNote.name} requiere una nota obligatoria.`);
        }

        if(this.withoutClient) {
          this.form.client = {
            name: "Cliente por defecto",
            email: null,
            phone: "0414409888391",
            address: "San diego",
            identification: "0000",
            balance: 0,
            typeClient: "a",
            companyName: null,
            id: 1
          }
          this.form.products = this.cart
          return this.form.post('/orders')
        }
        this.mode = 'client'
      } else if(this.mode === 'client') {
        this.form.products = this.cart
        this.form.post('/orders')
      } else if(this.mode === 'pay') {
        this.mode = 'print'
      }
    },

    removeProduct(product) {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.cart = this.cart.filter(p => p.id !== product.id)
      }
    },

    addCart(product) {
      if (product.quantity <= 0) {
        return alert('No hay stock disponible de este producto')
      }

      const existingProduct = this.cart.find(p => p.id === product.id)
      
      const defaultPrice = product?.prices?.length ? product.prices[0].price : 0
      const defaultGroupId = product?.prices?.length ? product.prices[0].group_id : null

      if (existingProduct) {
        existingProduct.quantity++
      } else {
        this.cart.push({
          ...product,
          stock: product.quantity,
          quantity: 1,
          price: defaultPrice,
          priceType: defaultGroupId ? `group-${defaultGroupId}` : 'custom',
          quick: false
        })
      }
    },

    onSearchProductsChange() {
      this.$inertia.get('/pos', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        }
      )
    },

    findClient() {
      this.$inertia.get('/pos', pickBy(this.form), { preserveState: true })

      setTimeout(() => {
        if (this.client) {
          this.textSearch = 'Buscar cliente'
          this.showClientCreate = true
          this.form.client = {
            name: this.client.name,
            email: this.client.email,
            phone: this.client.phone,
            address: this.client.address,
            identification: this.client.identification,
            balance: this.client.balance,
            typeClient: this.client.typeClient || 'a',
            companyName: this.client.companyName,
          }
        } else {
          alert('No se encontró el cliente. Puede registrarlo en el formulario.')
          this.showClientCreate = true
        }
      }, 1500)
    },

    clearAllClientFields() {
      this.form.client = {
        name: '',
        email: '',
        phone: '',
        address: '',
        identification: '',
        balance: 0,
        typeClient: 'a',
        companyName: null,
      }
      this.showClientCreate = false
      this.textSearch = 'Buscar cliente'

      if (typeof window !== 'undefined' && window.location.search && window.location.search.includes('client')) {
        this.$inertia.get('/pos', pickBy({ search: this.form.search }), {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        })
      }
    },

    resetBalance() {
      this.form.client.balance = 0
      if (typeof window !== 'undefined' && window.location.search && window.location.search.includes('client')) {
        this.$inertia.get('/pos', pickBy({ search: this.form.search }), {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        })
      }
    },
  },

  mounted() {
    document.getElementById("search").focus()
  }
}
</script>
